import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Inventory as InventoryIcon,
  Storage as StorageIcon,
  Http as HttpIcon,
  Code as CodeIcon,
  QuestionMark as QuestionMarkIcon,
  Memory as MemoryIcon,
} from "@mui/icons-material";
import ChooseModelIcon from "../assets/icons/choose-model";
import SetUpModelIcon from "../assets/icons/set-up-model";
import DashboardLayout from "../components/DashboardLayout";
import { DeploymentsSubnav } from "../features/omnimizer/components/DeploymentsSubnav";
import { LifecycleActions } from "../features/omnimizer/components/LifecycleActions";
import { deploymentApi } from "../services/omnimizer-api-service";
import { llmApi } from "../services/rag-service";
import { toast } from "sonner";
import { useStoredTheme } from "@/utils/theme-context";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "@tanstack/react-router";
import { DeploymentKpiStrip } from "@/features/omnimizer/components/DeploymentKpiStrip";
import { CatalogStatus } from "@/features/omnimizer/components/CatalogStatus";
import { useCatalogStatuses } from "@/features/omnimizer/catalog-sync";
import { canModifyDeployments } from "@/features/omnimizer/permissions";
import { LegacyDeploymentCallout } from "@/features/omnimizer/components/LegacyDeploymentCallout";
import { DeploymentStatusBadge } from "@/features/omnimizer/components/DeploymentStatusBadge";
import { lifecycleState } from "@/features/omnimizer/lifecycle";

// Source logo component
const SourceLogo = ({ source }) => {
  const { t } = useTranslation();

  // Determine which logo/icon to use based on source
  if (source === "hf") {
    return (
      <Avatar
        src="/providers/huggingface.svg"
        alt={t("deployments.huggingFace")}
        sx={{
          width: 16,
          height: 16,
          marginInlineEnd: 0.5,
          bgcolor: "var(--color-3)",
          border: "1px solid var(--color-71)",
        }}
      />
    );
  } else if (source === "s3") {
    return (
      <Avatar
        sx={{
          width: 16,
          height: 16,
          marginInlineEnd: 0.5,
          bgcolor: "var(--color-72)", // AWS orange color
        }}
      >
        <StorageIcon sx={{ fontSize: 18, color: "var(--color-3)" }} />
      </Avatar>
    );
  } else if (source === "http" || source === "https") {
    return (
      <Avatar
        sx={{
          width: 16,
          height: 16,
          marginInlineEnd: 0.5,
          bgcolor: "var(--color-73)", // Purple for HTTP
        }}
      >
        <HttpIcon sx={{ fontSize: 18, color: "var(--color-3)" }} />
      </Avatar>
    );
  } else if (source === "local") {
    return (
      <Avatar
        sx={{
          width: 16,
          height: 16,
          marginInlineEnd: 0.5,
          bgcolor: "var(--color-74)", // Green for local
        }}
      >
        <CodeIcon sx={{ fontSize: 18, color: "var(--color-3)" }} />
      </Avatar>
    );
  } else {
    // Default for unknown sources
    return (
      <Avatar
        sx={{
          width: 16,
          height: 16,
          marginInlineEnd: 0.5,
          bgcolor: "var(--color-71)",
        }}
      >
        <QuestionMarkIcon sx={{ fontSize: 18 }} />
      </Avatar>
    );
  }
};

// Skeleton card component for loading state
const DeploymentSkeletonCard = () => {
  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="circular" width={24} height={24} />
        </Box>
        <Skeleton variant="text" width="40%" height={24} sx={{ mt: 1 }} />
        <Box sx={{ mt: 2, mb: 1 }}>
          <Skeleton variant="rounded" width={80} height={24} />
        </Box>
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="90%" height={20} />
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="text" width="50%" height={20} />
        </Box>
        <Box sx={{ display: "flex", mt: 2, pt: 2 }}>
          <Skeleton
            variant="rounded"
            width={100}
            height={36}
            sx={{ marginInlineEnd: 1 }}
          />
          <Skeleton variant="rounded" width={100} height={36} />
        </Box>
      </CardContent>
    </Card>
  );
};

const Deployments = () => {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const theme = useTheme();
  const storedTheme = useStoredTheme();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const [deployments, setDeployments] = useState([]);
  const canDeploy = canModifyDeployments();
  const catalogStatuses = useCatalogStatuses(deployments);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deployMenuAnchor, setDeployMenuAnchor] = useState(null);
  const deployMenuOpen = Boolean(deployMenuAnchor);

  const statuses = [
    { label: t("deployments.allStatus"), value: "ALL" },
    ...["running", "starting", "stopping", "stopped", "sleeping", "failed"].map((state) => ({ label: t(`omnimizer.lifecycle.${state}`), value: state })),
  ];

  const selectStyle = {
    borderRadius: 0,
    backgroundColor: storedTheme.input.backgroundColor[theme.palette.mode],
    borderBottom: "1px solid",
    borderColor: storedTheme.input.borderColor[theme.palette.mode],
    ".MuiFilledInput-root": {
      borderRadius: 0,
      backgroundColor: storedTheme.input.backgroundColor[theme.palette.mode],
    },
    "&:before": {
      borderBottom: "1px solid",
      borderColor: storedTheme.input.borderColor[theme.palette.mode],
    },
    "&:hover:not(.Mui-disabled, .Mui-error):before": {
      borderBottom: "1px solid",
      borderColor: storedTheme.input.borderColor[theme.palette.mode],
    },
    "&:after": {
      borderBottom: "2px solid",
      borderColor: storedTheme.input.borderColor[theme.palette.mode],
    },
    "& input": {
      color: storedTheme.input.textColor[theme.palette.mode],
    },
    "& .MuiSvgIcon-root": {
      color: storedTheme.input.textColor[theme.palette.mode],
    },
    "& .MuiInputLabel-root": {
      color: storedTheme.input.textColor[theme.palette.mode],
    },
  };

  const selectMenuProps = {
    PaperProps: {
      sx: {
        "& .MuiMenuItem-root:hover": {
          backgroundColor: "rgba(19, 211, 158, 0.08)",
        },
        "& .MuiMenuItem-root.Mui-selected": {
          backgroundColor: "rgba(19, 211, 158, 0.16)",
          "&:hover": {
            backgroundColor: "rgba(19, 211, 158, 0.24)",
          },
        },
      },
    },
  };

  const darkTabStyle = {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "2.25rem",
    background:
      theme.palette.mode === "dark" ? "var(--color-76)" : "var(--color-75)",
    color: theme.palette.mode === "dark" ? "var(--color-3)" : "var(--color-11)",
    px: 1,
    py: 0.25,
  };

  useEffect(() => {
    fetchDeployments();
  }, []);

  const operationPending = deployments.some((deployment) => ["starting", "stopping"].includes(lifecycleState(deployment)));
  useEffect(() => {
    if (!operationPending) return;
    let disposed = false;
    let inFlight = false;
    const timer = window.setInterval(async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const response = await deploymentApi.listDeployments();
        if (!disposed) setDeployments(response.data);
      } catch {
        // Keep the last observed state; a transient poll failure is not a model failure.
      } finally { inFlight = false; }
    }, 3000);
    return () => { disposed = true; window.clearInterval(timer); };
  }, [operationPending]);

  const fetchDeployments = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const response = await deploymentApi.listDeployments();
      setDeployments(response.data);
    } catch (error) {
      console.error("Error fetching deployments:", error);
      toast.error(t("deployments.failedToLoadDeployments"));
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, deployment) => {
    setAnchorEl(event.currentTarget);
    setSelectedDeployment(deployment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFilterChange = (status) => {
    setStatusFilter(status);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDeployment) return;

    setDeleteLoading(true);
    try {
      // First check if this model is registered for RAG
      try {
        const config = await llmApi.getLLMConfigByExternalId(
          selectedDeployment.model_id,
        );

        // If the model is registered for RAG, delete it from the RAG service first
        if (config && config.id) {
          await llmApi.deleteLLMConfig(config.id);
        }
      } catch (ragError) {
        console.error("Error checking/unregistering model from RAG:", ragError);
        // Continue with deployment deletion even if RAG unregistration fails
      }

      // Proceed with normal deployment deletion
      await deploymentApi.deleteDeployment(selectedDeployment.model_id);
      toast.success(t("deployments.deploymentDeletedSuccess"));
      fetchDeployments();
    } catch (error) {
      console.error("Error deleting deployment:", error);
      toast.error(t("deployments.failedToDeleteDeployment"));
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeployMenuClick = (event) => {
    setDeployMenuAnchor(event.currentTarget);
  };

  const handleDeployMenuClose = () => {
    setDeployMenuAnchor(null);
  };

  const handleDeployOptionClick = (path) => {
    navigate(path);
    handleDeployMenuClose();
  };

  // Filter deployments based on search term and status filter
  const filteredDeployments = deployments.filter((deployment) => {
    const matchesSearch =
      deployment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deployment.model_id.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "ALL" ||
      lifecycleState(deployment) === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Add this function to format the date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString(
      i18n.resolvedLanguage || i18n.language,
    );
  };

  const OptionCard = ({ icon, title, description, buttonLabel, link }) => (
    <Box
      sx={{
        p: "2rem 1rem 1.5rem 1rem",
        border: `1px solid`,
        background: storedTheme.card.backgroundColor[theme.palette.mode],
        borderColor: storedTheme.card.borderColor[theme.palette.mode],
        borderRadius: storedTheme.card.borderRadius[theme.palette.mode],
        width: "100%",
        maxWidth: "25rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "40px",
          mb: 2,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: "1rem", fontWeight: 500, mb: 2 }}>
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontWeight: 400,
          opacity: 0.7,
          mb: 2,
          height: "2.25rem",
          overflow: "auto",
        }}
      >
        {description}
      </Typography>
      <Button
        fullWidth
        variant="contained"
        color="primary"
        sx={{
          borderRadius: "6.25rem",
          textTransform: "uppercase",
        }}
        onClick={() => handleDeployOptionClick(link)}
      >
        {buttonLabel}
      </Button>
    </Box>
  );

  return (
    <DashboardLayout title={t("deployments.title")}>
      <DeploymentsSubnav current="list" className="mb-4" />
      <DeploymentKpiStrip items={deployments} loading={loading} />
      {deployments.length ? (
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <FormControl variant="filled" fullWidth>
                <TextField
                  label={t("common.search")}
                  placeholder={t("deployments.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  variant="filled"
                  sx={selectStyle}
                  disabled={loading}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <FormControl variant="filled" fullWidth>
                <InputLabel>{t("common.status")}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t("common.status")}
                  onChange={(e) => {
                    handleFilterChange(e.target.value);
                  }}
                  disabled={loading}
                  sx={selectStyle}
                  MenuProps={selectMenuProps}
                >
                  {statuses.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                justifyContent: { xs: "center", md: "flex-end" },
                mt: { xs: 2, md: 0 },
              }}
            >
              <IconButton
                sx={{
                  padding: "1rem",
                  borderRadius: "0.75rem",
                  background:
                    theme.palette.mode === "dark"
                      ? "var(--color-10)"
                      : "var(--color-77)",
                }}
                onClick={fetchDeployments}
                title={t("common.refresh")}
              >
                <RefreshIcon className="rtl:scale-x-[-1]" />
              </IconButton>
              <Box>
                <Button
                  variant="contained"
                  endIcon={<ExpandMoreIcon />}
                  onClick={handleDeployMenuClick} disabled={!canDeploy}
                  sx={{
                    borderRadius: "5rem",
                    minWidth: {
                      xs: "100%",
                      sm: "100%",
                      md: "auto",
                      lg: "auto",
                    },
                  }}
                >
                  {t("deployments.deployModel")}
                </Button>
                <Menu
                  id="deploy-menu"
                  anchorEl={deployMenuAnchor}
                  open={deployMenuOpen}
                  onClose={handleDeployMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  sx={{
                    "& .MuiPaper-root": {
                      mt: 0.5,
                      borderRadius: 1.5,
                      minWidth: 200,
                      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
                      backgroundColor:
                        theme.palette.mode === "light"
                          ? "var(--color-3)"
                          : "var(--color-29)",
                      "& .MuiList-root": {
                        padding: "8px 0",
                      },
                    },
                  }}
                >
                  <MenuItem
                    onClick={() =>
                      handleDeployOptionClick("/predefined-models")
                    }
                    sx={{
                      py: 1.5,
                      px: 2,
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "light"
                            ? "rgba(0, 0, 0, 0.04)"
                            : "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <AddIcon fontSize="small" />
                      {t("deployments.preDefinedModels")}
                    </Box>
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      handleDeployOptionClick("/deployments/new")
                    }
                    sx={{
                      py: 1.5,
                      px: 2,
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "light"
                            ? "rgba(0, 0, 0, 0.04)"
                            : "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <InventoryIcon fontSize="small" />
                      {t("deployments.customDeployments")}
                    </Box>
                  </MenuItem>
                </Menu>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : null}

      {loading ? (
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid item xs={12} md={6} lg={4} key={`skeleton-${index}`}>
              <DeploymentSkeletonCard />
            </Grid>
          ))}
        </Grid>
      ) : filteredDeployments.length > 0 ? (
        <Grid container spacing={3}>
          {filteredDeployments.map((deployment) => (
            <Grid item xs={12} md={6} lg={4} key={deployment.model_id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="h2"
                      noWrap
                      sx={{ maxWidth: "80%" }}
                    >
                      {deployment.name}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          padding: "0.25rem 0.5rem",
                          borderRadius: "0.5rem",
                          border: "1px solid var(--color-78)",
                          color:
                            theme.palette.mode === "dark"
                              ? "var(--color-31)"
                              : "var(--color-27)",
                          fontSize: "0.75rem",
                          fontWeight: "400",
                        }}
                      >
                        {deployment.version}
                      </Box>

                      <DeploymentStatusBadge deployment={deployment} />
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("deployments.modelSource")}
                      </Typography>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          borderRadius: "0.5rem",
                          border: "1px solid var(--color-78)",
                          px: 0.5,
                          py: 0.25,
                        }}
                      >
                        <SourceLogo source={deployment?.model_source} />
                        <Typography variant="body2">
                          {deployment?.model_source === "hf"
                            ? t("deployments.huggingFace")
                            : deployment?.model_source === "s3"
                              ? t("deployments.s3Bucket")
                              : deployment?.model_source === "http"
                                ? t("deployments.httpUrl")
                                : deployment?.model_source ||
                                  t("common.unknown")}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("deployments.gpuCount")}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography variant="body2">
                          {deployment.resources?.gpu_count || 1}x
                        </Typography>
                        {deployment.resources?.gpu_type ? (
                          <Typography
                            variant="body2"
                            component="span"
                            sx={darkTabStyle}
                          >
                            <MemoryIcon
                              sx={{
                                height: "14px",
                                width: "14px",
                                marginInlineEnd: 0.25,
                                color:
                                  theme.palette.mode === "dark"
                                    ? "var(--color-3)"
                                    : "var(--color-11)",
                              }}
                            />
                            {deployment.resources?.gpu_type}
                          </Typography>
                        ) : null}
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("deployments.modelType")}
                      </Typography>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={darkTabStyle}
                      >
                        <MemoryIcon
                          sx={{
                            height: "14px",
                            width: "14px",
                            marginInlineEnd: 0.25,
                            color:
                              theme.palette.mode === "dark"
                                ? "var(--color-3)"
                                : "var(--color-11)",
                          }}
                        />
                        {deployment?.args?.is_llm === true
                          ? t("deployments.llm")
                          : t("deployments.deepLearning")}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("common.created")}
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(deployment.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">{t("omnimizer.overview.columns.catalog")}</Typography>
                      <CatalogStatus deployment={deployment} status={catalogStatuses.get(deployment.model_id)} compact />
                    </Grid>
                  </Grid>
                </CardContent>
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <IconButton
                    sx={{
                      padding: "1rem",
                      borderRadius: "0.75rem",
                      background:
                        theme.palette.mode === "dark"
                          ? "var(--color-10)"
                          : "var(--color-77)",
                    }}
                    size="small"
                    onClick={(e) => handleMenuOpen(e, deployment)}
                    aria-label={t("common.actions")}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Button component={RouterLink} to="/deployments/observability" search={{ deployment: deployment.model_id }} variant="text">
                    {t("omnimizer.overview.observe")}
                  </Button>
                  {canDeploy && <LifecycleActions deployment={deployment} onChanged={() => fetchDeployments({ quiet: true })} />}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : searchTerm || statusFilter !== "ALL" ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary" paragraph>
            {t("deployments.noMatchingDeployments")}
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color:
              theme.palette.mode === "light"
                ? "var(--color-10)"
                : "var(--color-4)",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1.7rem" }}>
              {t("deployments.createFirstDeployment")}
            </Typography>
            <Typography
              sx={{
                mb: 2,
                fontSize: "1rem",
                fontWeight: 400,
                opacity: 0.7,
              }}
            >
              {t("deployments.getStartedDescription")}
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
              <OptionCard
                icon={
                  <ChooseModelIcon
                    height={40}
                    width={40}
                    fill={
                      storedTheme.button.primary.backgroundColor[
                        theme.palette.mode
                      ]
                    }
                  />
                }
                title={t("omnimizer.nav.quick")}
                description={t("deployments.chooseFromCatalogDescription")}
                buttonLabel={t("omnimizer.nav.quick")}
                link="/predefined-models"
              />

              {canDeploy && <OptionCard
                icon={
                  <SetUpModelIcon
                    height={40}
                    width={40}
                    fill={
                      storedTheme.button.primary.backgroundColor[
                        theme.palette.mode
                      ]
                    }
                  />
                }
                title={t("omnimizer.advancedTitle")}
                description={t("omnimizer.advancedDescription")}
                buttonLabel={t("omnimizer.nav.deploy")}
                link="/deployments/new"
              />}
            </Box>
            <LegacyDeploymentCallout />
          </Box>
        </Box>
      )}

      {/* Deployment options menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate(`/deployments/${selectedDeployment?.model_id}`);
          }}
        >
          {t("deployments.viewDetails")}
        </MenuItem>
        {canDeploy && <MenuItem onClick={handleDeleteClick}>
          <DeleteIcon
            fontSize="small"
            sx={{ marginInlineEnd: 1, color: "error.main" }}
          />
          <Typography color="error">{t("common.delete")}</Typography>
        </MenuItem>}
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t("deployments.deleteDeployment")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("deployments.deleteDeploymentConfirm", {
              name: selectedDeployment?.name,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteLoading}
            startIcon={
              deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />
            }
          >
            {deleteLoading ? t("common.deleting") : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default Deployments;
